<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    name: {
        type: String,
        default: null
    },
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: String,
        default: ''
    },
    // Com titulo="", o campo fica sem título.
    titulo: {
        type: String,
        default: 'Descrição'
    },
    // Tooltip de ajuda ao lado do título.
    help: {
        type: String,
        default: null
    },
    placeholder: {
        type: String,
        default: 'Descreva aqui...'
    },
    linhas: {
        type: [Number, String],
        default: 4
    },
    // Cresce conforme o texto, a partir de `linhas`.
    autoResize: {
        type: Boolean,
        default: true
    },
    maxlength: {
        type: [Number, String],
        default: undefined
    },
    // Mostra a quantidade de caracteres digitados (e o limite, com `maxlength`).
    contador: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    obrigatorio: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

// Getter para acompanhar mudanças no nome (ex.: índice de item em useFieldArray após remover outro).
const campoForm = props.name ? useField(() => props.name) : null;

const texto = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? '',
    set: (value) => {
        if (campoForm) campoForm.value.value = value;
        emit('update:modelValue', value);
        emit('change', value);
    }
});

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div class="field mb-2" :class="props.classeColuna">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" :help="props.help" />
        <Textarea :id="props.id" v-model="texto" :rows="props.linhas" :autoResize="props.autoResize" style="resize: none" class="w-full"
            :disabled="props.disabled" :placeholder="props.placeholder" :maxlength="props.maxlength"
            :class="{ 'p-invalid': mensagemErro }" />
        <div v-if="props.contador" class="flex justify-content-between align-items-start gap-2">
            <SmallError v-if="mensagemErro" :error="mensagemErro" />
            <span class="text-500 text-xs ml-auto flex-shrink-0">
                {{ texto.length }}{{ props.maxlength ? `/${props.maxlength}` : ' caracteres' }}
            </span>
        </div>
        <SmallError v-else-if="mensagemErro" :error="mensagemErro" />
        <!-- Texto de apoio abaixo do campo (ex.: aviso de que a justificativa fica no histórico). -->
        <slot />
    </div>
</template>
